import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import { isTabBackgrounded, isPoolRecoveryInProgress } from '@utils/sessionNetworkCoordinator';
import { getNotifications, getNotificationsStreamUrl } from '@services/notifications.service';
import { NOTIFICATIONS_QUERY_KEY } from '@hooks/useNotifications';

const NOTIFICATIONS_REFETCH_THROTTLE_MS = 2000;
const STREAM_BOOTSTRAP_PAGE = 1;
const STREAM_BOOTSTRAP_LIMIT = 50;

function parseSseBlock(block) {
    const lines = String(block || '')
        .split('\n')
        .map((l) => l.replace(/\r$/, ''));

    let eventType = 'message';
    const dataLines = [];

    for (const line of lines) {
        if (!line || line.startsWith(':')) continue;
        if (line.startsWith('event:')) {
            eventType = line.slice('event:'.length).trim() || eventType;
            continue;
        }
        if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).trimStart());
            continue;
        }
    }

    if (!dataLines.length) return null;

    const rawData = dataLines.join('\n');
    let payload = rawData;
    try {
        payload = JSON.parse(rawData);
    } catch {
        // keep as string
    }

    return { eventType, payload };
}

function connectNotificationsStream(accessToken, { onEvent, onDone, onUnauthorized }) {
    const url = getNotificationsStreamUrl();
    const controller = new AbortController();

    fetch(url, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
            Connection: 'close',
        },
        signal: controller.signal,
        cache: 'no-store',
    })
        .then(async (response) => {
            if (!response.ok) {
                if (response.status === 401) {
                    onUnauthorized?.();
                    onDone();
                    return;
                }
                onDone();
                return;
            }
            const reader = response.body?.getReader();
            if (!reader) {
                onDone();
                return;
            }
            const decoder = new TextDecoder();
            let buffer = '';
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const blocks = buffer.split(/\n\n+/);
                    buffer = blocks.pop() ?? '';
                    for (const block of blocks) {
                        const parsed = parseSseBlock(block);
                        if (parsed) onEvent?.(parsed);
                    }
                }
            } finally {
                reader.releaseLock();
            }
            onDone();
        })
        .catch(() => {
            onDone();
        });

    return () => controller.abort();
}

function getEventTypeFromSse({ eventType, payload }) {
    if (eventType && eventType !== 'message') return eventType;
    if (payload && typeof payload === 'object') {
        return payload.type || payload.event || payload.eventType || eventType || 'message';
    }
    return eventType || 'message';
}

export function NotificationsStreamManager() {
    const queryClient = useQueryClient();
    const accessToken = useAuthStore((state) => state.accessToken);
    const abortRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const lastRefetchRef = useRef(0);
    const connectRef = useRef(null);

    const hasToken = !!accessToken;

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    const stopStream = useCallback(() => {
        clearReconnectTimer();
        if (abortRef.current) {
            try {
                abortRef.current();
            } catch {
                // ignore
            }
            abortRef.current = null;
        }
    }, [clearReconnectTimer]);

    const { isFetched, isLoading } = useQuery({
        queryKey: [...NOTIFICATIONS_QUERY_KEY, { page: STREAM_BOOTSTRAP_PAGE, limit: STREAM_BOOTSTRAP_LIMIT }],
        queryFn: () => getNotifications({ page: STREAM_BOOTSTRAP_PAGE, limit: STREAM_BOOTSTRAP_LIMIT }),
        enabled: hasToken && !isTabBackgrounded(),
        retry: (failureCount, error) => {
            if (error?.response?.status === 401) return false;
            return failureCount < 1;
        },
    });

    const initialFetchDone = hasToken && isFetched && !isLoading;

    useEffect(() => {
        const token = accessToken || useAuthStore.getState().accessToken;
        if (!token || !initialFetchDone) return undefined;

        const connect = () => {
            if (isTabBackgrounded()) return;
            if (typeof navigator !== 'undefined' && !navigator.onLine) return;
            if (isPoolRecoveryInProgress()) return;
            const currentToken = useAuthStore.getState().accessToken;
            if (!currentToken) return;

            stopStream();
            abortRef.current = connectNotificationsStream(currentToken, {
                onEvent: ({ eventType, payload }) => {
                    if (isTabBackgrounded()) return;
                    const resolvedType = getEventTypeFromSse({ eventType, payload });
                    if (resolvedType === 'rbac_role_permissions_updated') {
                        useAuthStore.getState().refreshMe?.().catch(() => {});
                        queryClient.invalidateQueries();
                        return;
                    }
                    if (resolvedType === 'heartbeat') {
                        return;
                    }
                    if (resolvedType === 'notification_new') {
                        if (typeof window !== 'undefined' && payload) {
                            window.dispatchEvent(new CustomEvent('app_notification_received', { detail: payload }));
                        }
                    }

                    const now = Date.now();
                    if (now - lastRefetchRef.current < NOTIFICATIONS_REFETCH_THROTTLE_MS) return;
                    lastRefetchRef.current = now;
                    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
                },
                onDone: () => {},
                onUnauthorized: () => {},
            });
        };

        connectRef.current = connect;
        connect();

        return () => {
            stopStream();
        };
    }, [accessToken, initialFetchDone, queryClient, stopStream]);

    return null;
}

export default NotificationsStreamManager;
